import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { expandWriteActions, isPathAllowed, WRITE_ACTION_BUNDLES, type WriteAction } from "@shared/rbac";
import { PAGE_WRITE_ACTIONS } from "@shared/sidebar-pages";
import type { PinSessionInfo } from "@shared/station";

const RBAC_CACHE_KEY = "crosbe-rbac-pages";
const RBAC_APIS_CACHE_KEY = "crosbe-rbac-apis";
const RBAC_ROLES_CACHE_KEY = "crosbe-rbac-roles";
const RBAC_CAPABILITIES_CACHE_KEY = "crosbe-rbac-capabilities";
/** #4952 round 3: the user id the four keys above were written for. */
const RBAC_OWNER_CACHE_KEY = "crosbe-rbac-owner";

/**
 * #4447: a PIN sign-in or Switch person changes WHO this browser is, so the
 * warm-load cache of the previous person's pages must not paint the sidebar
 * for the next one. Called before the full reload that follows either.
 * #4952 round 2: and after a sign-out, so the next person to sign in on this
 * browser is not shown the last one's tabs or capabilities.
 * #4952 round 3: and on the OAuth return and the session-expiry redirect
 * (RequireAuth); the owner goes with the four lists.
 */
export function clearRbacCache(): void {
  try {
    localStorage.removeItem(RBAC_OWNER_CACHE_KEY);
    localStorage.removeItem(RBAC_CACHE_KEY);
    localStorage.removeItem(RBAC_APIS_CACHE_KEY);
    localStorage.removeItem(RBAC_ROLES_CACHE_KEY);
    localStorage.removeItem(RBAC_CAPABILITIES_CACHE_KEY);
  } catch { /* ignore */ }
}

/**
 * #4952 round 3: the signed-in user the warm-load cache is read and written
 * for. localStorage is origin-wide, so without an owner one person's pages,
 * APIs, role ids and capabilities painted the next person's session while
 * /me was pending or degraded. RequireAuth binds session.user.id on every
 * render, before its children read the cache; every reader of the four keys
 * sits inside it. Null (nobody bound) reads no cache and writes none.
 */
let cacheOwner: string | null = null;

export function bindRbacCacheOwner(userId: string | null | undefined): void {
  cacheOwner = typeof userId === "string" && userId !== "" ? userId : null;
}

/** True when the cache on this browser was written for the bound user. A missing owner is no cache. */
function cacheIsOwned(): boolean {
  if (!cacheOwner) return false;
  try {
    return localStorage.getItem(RBAC_OWNER_CACHE_KEY) === cacheOwner;
  } catch {
    return false;
  }
}

/**
 * One of the four cached lists, for the bound user only. Only a list of
 * strings counts (#4952 round 2): anything else is no cache, so a hand-edited
 * or corrupted entry hides the gated surfaces rather than matching a
 * substring of a string.
 */
function getCachedList(key: string): string[] | null {
  if (!cacheIsOwned()) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === "string")) return parsed;
  } catch { /* ignore */ }
  return null;
}

export interface UserRoleInfo {
  /** #737: the PRIMARY role (precedence ladder) — display/back-compat. */
  role_id: string;
  role_slug: string;
  role_name: string;
  /** #737: ALL assigned roles; allowed_pages is already the union of them. */
  roles: { role_id: string; role_slug: string; role_name: string }[];
  allowed_pages: string[];
  /** #778: union of allowed_apis across roles. Display-only (server RBAC enforces);
   *  drives per-tab gating (useCanAccessApi / useVisibleTabs). */
  allowed_apis: string[];
  /** #687: union of write actions across roles. Display-only (requireWriteAction
   *  enforces); drives capability-gated surfaces like the RDTI review tab. */
  allowed_write_actions?: string[];
  /** Stable capability slugs. Display-only; server authorization is authoritative. */
  allowed_capabilities?: string[];
  /** #3307 panel: true when the server could not read the write actions (list is then []). */
  write_actions_error?: boolean;
  /** #593: universal pages a per-user revoke takes away (pass to isUniversalPage). */
  revoked_pages?: string[];
  is_admin: boolean;
  /** #4447: the station behind this session when it was minted by a PIN; null for Microsoft. Server-set. */
  pin_session?: PinSessionInfo | null;
}

/** #4447: the PIN session behind this browser's session, or null (a Microsoft sign-in, or not yet known). */
export function usePinSession(): PinSessionInfo | null {
  const { data } = useUserRole();
  return data?.pin_session ?? null;
}

function getCachedPages(): string[] | null {
  return getCachedList(RBAC_CACHE_KEY);
}

function getCachedApis(): string[] | null {
  return getCachedList(RBAC_APIS_CACHE_KEY);
}

/** #4952 round 2: the last cleanly read capability list. */
function getCachedCapabilities(): string[] | null {
  return getCachedList(RBAC_CAPABILITIES_CACHE_KEY);
}

function getCachedRoleIds(): string[] | null {
  return getCachedList(RBAC_ROLES_CACHE_KEY);
}

/**
 * #4922 round 2: every /api/rbac/me request, the shell's (useUserRole) or an
 * admin route check's, takes the next number when it is sent. The answer of
 * the newest request settled so far holds ["rbac","me"] and the warm-load
 * cache, so an answer that lands late cannot overwrite one sent after it.
 */
let lastMeRequest = 0;
let settledMe: { request: number; data: UserRoleInfo } | null = null;

export function beginMeRequest(): number {
  return ++lastMeRequest;
}

/** The request number whose answer ["rbac","me"] holds now (0 before any). */
export function settledMeRequest(): number {
  return settledMe?.request ?? 0;
}

/**
 * Settle the answer to request `request`: it wins unless a newer request has
 * already settled, and only the winner is written to the warm-load cache.
 * Returns the payload ["rbac","me"] should hold.
 */
export function settleUserRole(request: number, data: UserRoleInfo): UserRoleInfo {
  if (settledMe && settledMe.request > request) return settledMe.data;
  settledMe = { request, data };
  writeRbacCache(data);
  return data;
}

/**
 * Cache the settled answer so the next page load has it instantly, stamped
 * with the bound user (#4952 round 3). A snapshot written for anyone else, or
 * for nobody, is dropped whole first, and the owner is written last, so a
 * write that fails part way leaves no owned cache behind.
 */
function writeRbacCache(data: UserRoleInfo): void {
  const owner = cacheOwner;
  if (!owner) return;
  try {
    if (localStorage.getItem(RBAC_OWNER_CACHE_KEY) !== owner) clearRbacCache();
    localStorage.setItem(RBAC_CACHE_KEY, JSON.stringify(data.allowed_pages));
    localStorage.setItem(
      RBAC_ROLES_CACHE_KEY,
      JSON.stringify(data.roles.map((role) => role.role_id)),
    );
    // #4952 round 2 and 3: a /me whose capability read failed
    // (write_actions_error) sends [] capabilities, and the server derives the
    // Ostendo API paths from that [] (rbac.ts readRoutePathsForCapabilities),
    // so neither half is a revoke. Both are written only from a clean read,
    // together, and a degraded /me keeps the last clean pair.
    if (!data.write_actions_error) {
      localStorage.setItem(RBAC_APIS_CACHE_KEY, JSON.stringify(data.allowed_apis ?? []));
      localStorage.setItem(RBAC_CAPABILITIES_CACHE_KEY, JSON.stringify(data.allowed_capabilities ?? []));
    }
    localStorage.setItem(RBAC_OWNER_CACHE_KEY, owner);
  } catch {
    // A half-written snapshot is not left to be read as whole.
    clearRbacCache();
  }
}

/**
 * GET /api/rbac/me for ["rbac","me"], settled against any newer request.
 * #4922: `signal` is the calling query's, so a cancelled query aborts the
 * request rather than leaving it running.
 */
export async function fetchUserRole(signal?: AbortSignal): Promise<UserRoleInfo> {
  const request = beginMeRequest();
  const data = await apiFetch<UserRoleInfo>("/api/rbac/me", undefined, signal);
  return settleUserRole(request, data);
}

export function useUserRole() {
  return useQuery({
    queryKey: ["rbac", "me"],
    queryFn: ({ signal }) => fetchUserRole(signal),
    staleTime: 10 * 60 * 1000, // 10 min — role rarely changes
  });
}

/** All assigned role IDs, with the same warm-load cache as pages/APIs. */
export function useAssignedRoleIds(): string[] | null {
  const { data } = useUserRole();
  if (data) return data.roles.map((role) => role.role_id);
  return getCachedRoleIds();
}

/**
 * Get allowed pages — from API data, localStorage cache, or null if unknown.
 */
export function useAllowedPages(): string[] | null {
  const { data } = useUserRole();
  if (data) return data.allowed_pages;
  return getCachedPages();
}

/**
 * #593: universal pages a per-user revoke takes away. Empty until /me answers
 * (the server refuses the page's API either way).
 */
const NO_REVOKED_PAGES: string[] = [];
export function useRevokedPages(): string[] {
  const { data } = useUserRole();
  return data?.revoked_pages ?? NO_REVOKED_PAGES;
}

/**
 * Check if the current user can access a given page path.
 * Returns true only if role data confirms access. Returns false while loading
 * (unless localStorage cache is available).
 */
export function useCanAccess(path: string): boolean {
  const pages = useAllowedPages();
  if (!pages) return false; // Hide until role is known
  return isPathAllowed(path, pages);
}

/**
 * Whether the current user has admin privileges (server-side isAdmin check).
 * Used to hide admin-only UI elements like User Management link.
 */
export function useIsAdmin(): boolean {
  const { data } = useUserRole();
  return data?.is_admin ?? false;
}

/**
 * #687: whether the current user holds a write action (e.g. "rdti_manage").
 * Display-only — shows/hides capability-gated surfaces; the server's
 * requireWriteAction gate is the real enforcement.
 *
 * #3307: reads allowed_write_actions only, never is_admin. /api/rbac/me
 * resolves that list with per-user revokes applied, and an admin's list is
 * the "*" wildcard, so a plain admin still sees every control and a revoked
 * admin loses exactly the revoked one, matching hasWriteAction on the server.
 *
 * Failure behaviour, for every caller including an admin:
 *   - /me still loading: false (controls hidden, no notice).
 *   - /me request failed, or loaded with write_actions_error: false (controls
 *     hidden), and usePermissionsLoadError() returns the one-line notice the
 *     app shell shows, so a failed read does not look like a revoke.
 */
export function useCanWriteAction(action: string): boolean {
  const { data } = useUserRole();
  return holdsWriteAction(data, action);
}

function holdsWriteAction(data: UserRoleInfo | undefined, action: string): boolean {
  if (!data) return false;
  // #3282 round 2: expand write bundles (rdti_log, rdti_manage) with the SAME
  // shared table the server's isWriteAllowed reads, so a user holding only a
  // bundle sees every control the server admits them to.
  const actions = expandWriteActions((data.allowed_write_actions ?? []) as (WriteAction | "*")[]);
  return actions.includes("*") || actions.includes(action as WriteAction);
}

export interface PageWriteAccess {
  /** The permissions read settled cleanly, so `missing` is a fact, not a gap. */
  known: boolean;
  /** Same answer as useCanWriteAction(action). False until known. */
  can: (action: WriteAction) => boolean;
  /** The page's write actions (PAGE_WRITE_ACTIONS) this user lacks; a bundle entry is held through any of its units (#3621). [] until known. */
  missing: WriteAction[];
  /** Known, and the user holds none of the page's write actions. */
  viewOnly: boolean;
}

/**
 * #3306: which of a page's write actions the user holds, read from the same
 * PAGE_WRITE_ACTIONS map that decides Data Entry membership. A role can hold a
 * page (qa_tech on /qa-entry) without any of its writes, and a form it cannot
 * save is a dead end: the page renders its write controls only through
 * `can`, and names `missing` in one line. Display-only; the server's
 * requireWriteAction is the real gate.
 *
 * While /me loads, or when it failed, nothing is admitted and nothing is
 * called missing: a failed read already has its own notice
 * (usePermissionsLoadError) and must not read as "view only".
 */
export function usePageWriteAccess(path: string): PageWriteAccess {
  const { data } = useUserRole();
  const known = !!data && !data.write_actions_error;
  const can = (action: WriteAction) => holdsWriteAction(data, action);
  const pageActions = PAGE_WRITE_ACTIONS[path] ?? [];
  const missing = known ? pageActions.filter((a) => !holdsPageWriteAction(data, a)) : [];
  const viewOnly = known && pageActions.length > 0 && missing.length === pageActions.length;
  return { known, can, missing, viewOnly };
}

/**
 * #3621: a page entry that names a write BUNDLE (/rdti -> rdti_log) is held
 * when the user holds the bundle or ANY of its units. Since #3450, /me drops a
 * bundle name once one of its units is revoked and lists the held units on
 * their own, so an exact match would read a user holding four of five units
 * as view-only on a page they still write from. `can` stays exact (each
 * control asks for its own unit); only page-level membership widens.
 */
function holdsPageWriteAction(data: UserRoleInfo | undefined, action: WriteAction): boolean {
  if (holdsWriteAction(data, action)) return true;
  if (!Object.hasOwn(WRITE_ACTION_BUNDLES, action)) return false;
  const units: readonly WriteAction[] = WRITE_ACTION_BUNDLES[action as keyof typeof WRITE_ACTION_BUNDLES];
  return units.some((unit) => holdsWriteAction(data, unit));
}

export const PERMISSIONS_LOAD_ERROR = "Could not load your permissions. Reload the page to see your edit controls.";

/**
 * #3307 panel: the notice for a FAILED permissions read (the /me request
 * errored, or /me could not read the write actions). Null while loading and
 * when the read succeeded, so a revoke stays silent and loading stays quiet.
 */
export function usePermissionsLoadError(): string | null {
  const { data, isError } = useUserRole();
  if (isError || data?.write_actions_error) return PERMISSIONS_LOAD_ERROR;
  return null;
}

/**
 * Exact match, same as the server's hasPortableCapability (#2750): a "*"
 * entry in allowed_capabilities is not a supported shape and grants nothing.
 * Admin is admin through is_admin, not through a wildcard capability.
 * Display-only; the server re-checks every capability-gated route.
 *
 * #4952 round 2: where the list comes from (heldCapabilities):
 *   - /me settled cleanly: its allowed_capabilities, [] included (a revoke).
 *   - /me still loading, or the request failed: the warm-load cache, the
 *     same fallback useAllowedApis takes, so a reload keeps the surfaces.
 *   - /me settled with write_actions_error: its [] is a failed read, so the
 *     cache too, and usePermissionsLoadError's notice shows.
 *   - no cache in either case: false (hidden, fail closed).
 * #4952 round 3: "the cache" is the bound user's own (getCachedList); another
 * person's, or one with no owner, is no cache.
 */
export function useHasCapability(capability: string): boolean {
  const { data } = useUserRole();
  return heldCapabilities(data)?.includes(capability) ?? false;
}

function heldCapabilities(data: UserRoleInfo | undefined): string[] | null {
  if (!data || data.write_actions_error) return getCachedCapabilities();
  return data.allowed_capabilities ?? [];
}

/**
 * Get allowed API prefixes: from API data, the localStorage cache, or null if unknown.
 *
 * #4952 round 3: a /me with write_actions_error lacks every API path the
 * server derives from capabilities (it clears them before the union, rbac.ts
 * /me), so in that state this reads the bound user's last clean list, the
 * same snapshot heldCapabilities reads, and both halves of a
 * capability-gated tab agree. With no such snapshot it falls back to the
 * degraded list: the role rows' own paths, a subset of what a clean read
 * sends, so nothing shows that a clean read would hide, and a capability
 * gate beside it stays closed (heldCapabilities is null).
 */
export function useAllowedApis(): string[] | null {
  const { data } = useUserRole();
  if (data && !data.write_actions_error) return data.allowed_apis ?? [];
  const cached = getCachedApis();
  if (cached || !data) return cached;
  return data.allowed_apis ?? [];
}

/**
 * #778: whether the current user's role can call a given API path. Used to gate
 * in-page TABS whose data endpoint is role-restricted (e.g. the Purchase Orders /
 * Inventory tabs call /api/ostendo/*, reachable only by full-access roles).
 *
 * Fail-closed while the role is unknown (apis === null) — matches useCanAccess's
 * "hide until role is known", so a gated tab never flashes before we know the role
 * can serve it. An undefined requirement means "no gate" → always true.
 */
export function useCanAccessApi(apiPath: string | undefined): boolean {
  const apis = useAllowedApis();
  if (!apiPath) return true; // no capability required → always visible
  if (!apis) return false; // role unknown → hide the gated tab until we know
  return isPathAllowed(apiPath, apis);
}

/**
 * #778: reusable per-tab RBAC gate for the merged Hubs (Materials / Shifts /
 * Raw Materials Analytics). A tab may declare `requiresApi` — the endpoint its content
 * calls; tabs the current role can't serve are DROPPED so they render hidden,
 * not broken. Tabs with no `requiresApi` are always kept. A Hub whose tabs are
 * all equally accessible gets an unchanged list back (the mechanism is a no-op
 * there — that's fine). Consumers fall a hidden deep-link back to the first
 * visible (default) tab.
 */
export function useVisibleTabs<T extends { requiresApi?: string }>(tabs: T[]): T[] {
  const apis = useAllowedApis();
  return tabs.filter((t) => {
    if (!t.requiresApi) return true; // ungated tab — always visible
    if (!apis) return false; // role unknown → hide gated tab until known
    return isPathAllowed(t.requiresApi, apis);
  });
}
